<template>
  <div class="dashboard">
    <!-- 顶部统计卡片：根据角色展示不同指标 -->
    <div class="stat-cards">
      <div v-for="card in statCards" :key="card.key" class="stat-card" :class="{ 'stat-card-clickable': card.path }" @click="goCard(card)">
        <div class="stat-icon" :style="{ background: card.color }">
          <a-icon :type="card.icon" />
        </div>
        <div class="stat-info">
          <div class="stat-label">{{ card.label }}</div>
          <div class="stat-value">{{ card.value }}</div>
        </div>
      </div>
    </div>

    <!-- 发送趋势图：管理员 / 开发者可见 -->
    <a-card v-if="canViewSendStatistic" class="section-card" :bordered="false">
      <div slot="title" class="section-header">
        <span>发送趋势</span>
        <a-select v-model="offsetValue" size="small" style="width: 140px" @change="onOffsetChange">
          <a-select-option v-for="item in offsetOptions" :key="item.value" :value="item.value">{{ item.label }}</a-select-option>
        </a-select>
      </div>
      <stacked-area-chart v-if="chartData.inited" :x-data="chartData.xData" :series-data="chartData.seriesData" id="dashboard-chart" style="height: 320px" />
      <div v-else class="chart-empty">加载中...</div>
    </a-card>

    <!-- 快捷入口：根据角色展示不同功能入口 -->
    <a-card class="section-card" :bordered="false">
      <div slot="title" class="section-header">
        <span>快捷入口</span>
      </div>
      <div class="quick-links">
        <div v-for="link in quickLinks" :key="link.path" class="quick-link" @click="go(link.path)">
          <a-icon :type="link.icon" class="quick-icon" />
          <div class="quick-label">{{ link.label }}</div>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import StackedAreaChart from './components/stackedAreaChart.vue'
import { getMessageStatics, getNoticeHistoryStatistic } from '@/api/notice'
import { getUserTodoStatics } from '@/api/user-todo'
import { ROLES, getRole, isAdminOrDev } from '@/utils/permission'

// 网关类型（通知类型）色相池，按出现顺序稳定分配
const GATEWAY_COLORS = ['#1890ff', '#52c41a', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96', '#2f54eb', '#a0d911']

// 生成同色系浅色，用于区分同一网关的「失败」段
function lighten(hex, ratio) {
  const n = parseInt(hex.slice(1), 16)
  const mix = c => Math.round(c + (255 - c) * ratio)
  const toHex = v => (v < 16 ? '0' : '') + v.toString(16)
  return '#' + toHex(mix((n >> 16) & 255)) + toHex(mix((n >> 8) & 255)) + toHex(mix(n & 255))
}

export default {
  name: 'Dashboard',
  components: {
    StackedAreaChart
  },
  data() {
    return {
      offsetOptions: [
        { value: 1, label: '过去1小时', interval: '1m' },
        { value: 12, label: '过去12小时', interval: '1h' },
        { value: 24, label: '过去24小时', interval: '1h' },
        { value: 7 * 24, label: '过去7天', interval: '1d' },
        { value: 30 * 24, label: '过去30天', interval: '1d' }
      ],
      offsetValue: 7 * 24,
      allSuccess: 0,
      allFailure: 0,
      messageTotal: 0,
      todoTotal: 0,
      chartData: {
        xData: [],
        seriesData: [],
        inited: false
      },
      queryParams: {
        start_time: '',
        end_time: '',
        interval: ''
      }
    }
  },
  computed: {
    role() {
      return getRole()
    },
    // 管理员 / 开发者可查看发送统计与趋势图；普通用户仅看个人数据
    canViewSendStatistic() {
      return isAdminOrDev()
    },
    // 统计卡片：根据角色差异化；带 path 的卡片可点击跳转对应明细页
    statCards() {
      // 时间范围一并透传给通知历史页，保证跳转后筛选条件一致
      const timeQuery = { start_time: this.queryParams.start_time, end_time: this.queryParams.end_time }
      // 管理员 / 开发者：发送统计 + 个人数据
      if (this.canViewSendStatistic) {
        return [
          {
            key: 'total',
            label: '发送总量',
            value: this.allSuccess + this.allFailure,
            color: '#409eff',
            icon: 'rocket',
            path: '/notice-history',
            query: timeQuery
          },
          {
            key: 'success',
            label: '发送成功',
            value: this.allSuccess,
            color: '#67c23a',
            icon: 'check-circle',
            path: '/notice-history',
            query: { status: 1, ...timeQuery }
          },
          {
            key: 'failure',
            label: '发送失败',
            value: this.allFailure,
            color: '#f56c6c',
            icon: 'close-circle',
            path: '/notice-history',
            query: { status: 2, ...timeQuery }
          },
          { key: 'message', label: '我的消息', value: this.messageTotal, color: '#e6a23c', icon: 'message', path: '/my-message' },
          { key: 'todo', label: '我的待办', value: this.todoTotal, color: '#13c2c2', icon: 'unordered-list', path: '/todo-workbench' }
        ]
      }
      // 普通用户：仅个人数据
      return [
        { key: 'message', label: '我的消息', value: this.messageTotal, color: '#409eff', icon: 'message', path: '/my-message' },
        { key: 'todo', label: '我的待办', value: this.todoTotal, color: '#e6a23c', icon: 'unordered-list', path: '/todo-workbench' }
      ]
    },
    // 快捷入口：根据角色差异化
    quickLinks() {
      // 管理员：管理类入口
      if (this.role === ROLES.ADMIN) {
        return [
          { label: '通道配置', path: '/configuration', icon: 'setting' },
          { label: '通道组管理', path: '/ChannelGroupManage', icon: 'deployment-unit' },
          { label: '消息组管理', path: '/message-group-manager', icon: 'team' },
          { label: '模板管理', path: '/TemplateManage', icon: 'file-text' },
          { label: '事件日志', path: '/event-log', icon: 'line-chart' },
          { label: '统计报表', path: '/statistic-report', icon: 'pie-chart' },
          { label: '通讯录', path: '/Contact', icon: 'user' },
          { label: 'API Token', path: '/Token', icon: 'key' }
        ]
      }
      // 开发者：个人数据 + 开发 / 监控入口
      if (this.role === ROLES.DEV) {
        return [
          { label: '我的消息', path: '/my-message', icon: 'message' },
          { label: '待办工作台', path: '/todo-workbench', icon: 'unordered-list' },
          { label: '事件日志', path: '/event-log', icon: 'line-chart' },
          { label: '通知历史', path: '/notice-history', icon: 'history' },
          { label: '统计报表', path: '/statistic-report', icon: 'pie-chart' },
          { label: 'API Token', path: '/Token', icon: 'key' }
        ]
      }
      // 普通用户：仅个人入口
      return [
        { label: '我的消息', path: '/my-message', icon: 'message' },
        { label: '待办工作台', path: '/todo-workbench', icon: 'unordered-list' }
      ]
    }
  },
  created() {
    this.onOffsetChange()
  },
  methods: {
    onOffsetChange() {
      const option = this.offsetOptions.find(e => e.value === this.offsetValue)
      const end = new Date()
      const start = new Date(end.getTime() - this.offsetValue * 3600 * 1000)
      this.queryParams.end_time = this.formatDate(end)
      this.queryParams.start_time = this.formatDate(start)
      // 时间分桶粒度随所选范围自动切换（用户无需另外选择）
      this.queryParams.interval = option ? option.interval : ''

      this.getMessageTotal()
      this.getTodoTotal()
      if (this.canViewSendStatistic) {
        this.initChartData()
      }
    },
    formatDate(date) {
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      )
    },
    initChartData() {
      this.allSuccess = 0
      this.allFailure = 0
      this.chartData.inited = false
      getNoticeHistoryStatistic(this.queryParams).then(res => {
        const list = res?.data?.list || []

        // 卡片用汇总值：对全部 (时间桶 × 网关) 求和
        list.forEach(item => {
          this.allSuccess += item.success || 0
          this.allFailure += item.failure || 0
        })

        // x 轴：时间桶去重后升序（后端按时间格式化字符串返回，可直接字典序排序）
        const xData = Array.from(new Set(list.map(item => item.time || ''))).sort()
        // 网关类型（通知类型）：决定色相，首次出现的顺序即配色顺序，保证刷新后颜色稳定
        const gateways = Array.from(new Set(list.map(item => item.gateway || '未知')))
        // 以 (时间桶, 网关) 建索引，把稀疏结果对齐到连续的 x 轴
        const cell = {}
        list.forEach(item => {
          cell[`${item.time || ''}|${item.gateway || '未知'}`] = item
        })
        const valueOf = (time, gateway, field) => {
          const hit = cell[`${time}|${gateway}`]
          return hit ? hit[field] || 0 : 0
        }

        // 每个网关两条系列（成功/失败）共用同一堆叠：同一网关同色系，成功为原色、失败为浅色
        const seriesData = []
        gateways.forEach((gateway, i) => {
          const base = GATEWAY_COLORS[i % GATEWAY_COLORS.length]
          seriesData.push({
            name: `${gateway}-成功`,
            color: base,
            data: xData.map(t => valueOf(t, gateway, 'success'))
          })
          seriesData.push({
            name: `${gateway}-失败`,
            color: lighten(base, 0.55),
            data: xData.map(t => valueOf(t, gateway, 'failure'))
          })
        })

        this.chartData.xData = xData
        this.chartData.seriesData = seriesData
        this.chartData.inited = true
      })
    },
    getMessageTotal() {
      getMessageStatics().then(res => {
        this.messageTotal = res?.data?.count || 0
      })
    },
    getTodoTotal() {
      getUserTodoStatics().then(res => {
        this.todoTotal = res?.data?.count || 0
      })
    },
    go(path) {
      this.$router.push(path)
    },
    // 统计卡片点击：跳转到对应明细页（不带 path 的卡片不响应）
    goCard(card) {
      if (!card.path) return
      this.$router.push({ path: card.path, query: card.query || {} })
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 16px;
}
.stat-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}
.stat-card {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.stat-card-clickable {
  cursor: pointer;
  transition:
    box-shadow 0.2s,
    transform 0.2s;
}
.stat-card-clickable:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}
.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 26px;
  margin-right: 16px;
}
.stat-info {
  flex: 1;
}
.stat-label {
  color: #909399;
  font-size: 13px;
  margin-bottom: 6px;
}
.stat-value {
  font-size: 26px;
  font-weight: 600;
  color: #303133;
}
.section-card {
  margin-bottom: 16px;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
}
.chart-empty {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
}
.quick-links {
  display: flex;
  flex-wrap: wrap;
}
.quick-link {
  width: 120px;
  padding: 20px 8px;
  text-align: center;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
}
.quick-link:hover {
  background: #f5f7fa;
}
.quick-icon {
  font-size: 28px;
  color: #409eff;
}
.quick-label {
  margin-top: 8px;
  color: #606266;
}
</style>
